import './AdRow.scss';

import { bem } from 'mission-control-common-components';
import React from 'react';

import { DcmTagSheetCreativeSource } from '../../../../../../apis/campaignGraphqlManager/dcmTagSheet/types';
import { formatStringDate } from '../../../../../../utils/formatting';
import type { DragHandlers, TagSheetAd } from '../../types';
import type { AdValidationError } from '../../validation/types';
import EditableUrlField from '../EditableUrlField/EditableUrlField';
import ExpandToggle from '../ExpandToggle/ExpandToggle';
import AdSettingsMenu from './AdSettingsMenu/AdSettingsMenu';

const [block, element] = bem('line-items-ad-row');

type AdRowProps = {
  ad: TagSheetAd;
  dragHandlers?: DragHandlers;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClone: () => void;
  onAddTag: () => void;
  onDelete: () => void;
  onEditAd: (updatedAd: TagSheetAd) => void;
  error?: AdValidationError;
  readonly?: boolean;
};

const AdRow = ({
  ad,
  dragHandlers,
  isExpanded,
  onToggleExpand,
  onClone,
  onAddTag,
  onDelete,
  onEditAd,
  error,
  readonly = false,
}: AdRowProps): React.JSX.Element => {
  const hasTags = ad.tags.length > 0;
  const typeErrorMessage = error?.adType ?? error?.creativeType ?? error?.unsupportedCreativeType;
  const hasTypeError = Boolean(typeErrorMessage);
  const hasDateError = Boolean(error?.dateRange);
  const isDraggable = !readonly && dragHandlers !== undefined;

  return (
    <tr
      className={block(readonly ? 'readonly' : '')}
      draggable={isDraggable}
      onDragStart={isDraggable ? dragHandlers?.onDragStart(ad.id) : undefined}
      onDragEnd={isDraggable ? dragHandlers?.onDragEnd : undefined}
    >
      <td className={element('expand-cell')}>
        {hasTags ? (
          <ExpandToggle isExpanded={isExpanded} onToggle={onToggleExpand} />
        ) : (
          <span className={element('toggle-placeholder')} />
        )}
      </td>
      <td className={element('placement-id')}>{ad.placementId}</td>
      <td className={element('placement-name')}>{ad.placementName}</td>
      <td className={element('ad-type', hasTypeError ? 'error' : '')}>
        <span>{ad.adType.displayName}</span>
        {hasTypeError && (
          <span className={element('error-message')} data-testid="ad-type-error">
            {typeErrorMessage}
          </span>
        )}
      </td>
      <td className={element('name-cell')}>
        <EditableUrlField
          value={ad.adName}
          placeholder="Click to Enter Ad Name"
          onSave={(adName): void => onEditAd({ ...ad, adName })}
          testId="ad-name-field"
          error={!!error?.adName}
          readonly={readonly}
        />
      </td>
      <td className={element('creative')}>
        {ad.creative.source === DcmTagSheetCreativeSource.VAST && (
          <span className={element('creative-wrapper')}>
            <span className={element('creative-badge')}>VAST</span>
            <EditableUrlField
              value={ad.creative.creativeUrl || ''}
              placeholder="Click to Enter Vast URL"
              onSave={(creativeUrl): void => onEditAd({ ...ad, creative: { ...ad.creative, creativeUrl } })}
              testId="ad-vast-field"
              error={!!error?.creativeUrl}
              errorMessage={ad.creative.creativeUrl ? error?.creativeUrl : undefined}
              readonly={readonly}
            />
          </span>
        )}
      </td>
      <td className={element('date', hasDateError ? 'error' : '')}>{formatStringDate(ad.startDate)}</td>
      <td className={element('date', hasDateError ? 'error' : '')}>
        {formatStringDate(ad.endDate)}
        {error?.dateRange && (
          <span className={element('error-message')} data-testid="ad-date-error">
            {error.dateRange}
          </span>
        )}
      </td>
      <td className={element('settings')}>
        {!readonly && <AdSettingsMenu onClone={onClone} onAddTag={onAddTag} onDelete={onDelete} />}
      </td>
    </tr>
  );
};

export default AdRow;
